import {
  FileTextOutlined,
  LeftOutlined,
  RightOutlined,
} from '@ant-design/icons'
import {
  Button,
  DatePicker,
  Form,
  Input,
  List,
  Tabs,
  Tooltip,
  message,
} from 'antd'
import { toJS } from 'mobx'
import { inject, observer } from 'mobx-react'
import moment from 'moment'
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Helmet } from 'react-helmet/es/Helmet'
import { EmptyText, UrlifiedText } from '../../components/Common/CellText'
import CounterCard from '../../components/CounterCard/CounterCard'
import EmptyContent from '../../components/EmptyContent'
import SpinnerInlineComponent from '../../components/SpinnerInlineComponent'
import { systemName, useSSO } from '../../config'
import {
  ACL_ACTION_TYPE,
  DD_MM,
  HHhMM,
  ISO_DATE_FORMAT,
  MODULE_CODE,
} from '../../constants'
import date_utils from '../../date_utils'
import DashboardLayout from '../../layouts/DashboardLayout'
import utils from '../../utils'
import {
  buildImsatWorkScheduleCompanies,
  fetchWorkScheduleList,
  getDefaultWorkScheduleCompanyKey,
  resolveWorkScheduleMenuKey,
} from '../../utils/imsatWorkScheduleUtils'
import validator from '../../validator'
import { LoginWrapper } from '../PortalPageV2/PortalPageV2Styled'
import {
  CardWrapper,
  DayTab,
  DescriptionLink,
  GeneralNotif,
  ItemCalendarContent,
  RowWrapper,
  WorkScheduledCard,
  WorkScheduledItem,
} from './DashboardPageV2Styled'
import './index.css'

const { TabPane } = Tabs

const DashboardPageV2 = props => {
  const {
    history,
    location,
    workScheduleStore,
    parentWorkScheduleStore,
    statisticStore,
    fileStore,
    authenticationStore,
    accountStore,
    commandStore,
    notificationStore,
    userStore,
    moduleStore,
    aclStore,
    tintucStore,
    companyStore,
    infoOptionStore,
  } = props

  const { currentUser, isAccountAdmin, isSuperAdmin } = authenticationStore
  const { aclActionsByUser } = aclStore
  const { companyList } = companyStore
  const { sideMenuCounter, dashboardSideMenuCounter } = commandStore

  const [newsList, setNewsList] = useState([])
  const [workScheduleList, setWorkScheduledList] = useState([])
  const [selectedKey, setSelectedKey] = useState(null)
  const [activeDayTabKey, setActiveDayTabKey] = useState(null)
  const hasInitializedSelectedKey = useRef(false)

  const [days, setDays] = useState(date_utils.weekRange(date_utils.current()))
  const [isLoadingCalendar, setIsLoadingCalendar] = useState(false)
  const [isLoadingSidemenuCounter, setIsLoadingSidemenuCounter] = useState(
    false
  )
  const [isLoadingTinTuc, setIsLoadingTinTuc] = useState(false)
  //
  const [confirmLoading, setConfirmLoading] = useState(false)
  const [, setShowDialog] = useState(false)

  const permissionViewList = useMemo(
    () => toJS(workScheduleStore.workSchedulePermissionView),
    [workScheduleStore.workSchedulePermissionView]
  )

  const imsatCompanies = useMemo(
    () =>
      buildImsatWorkScheduleCompanies(
        infoOptionStore.infoOptionList,
        companyList,
        permissionViewList,
        currentUser?.company?.code
      ),
    [
      infoOptionStore.infoOptionList,
      companyList,
      permissionViewList,
      currentUser?.company?.code,
    ]
  )

  const workScheduleByDay = useMemo(() => {
    const scheduleByDay = {}

    workScheduleList.forEach(workSchedule => {
      const strDay = moment(workSchedule.start_at).format(DD_MM)
      if (!scheduleByDay[strDay]) {
        scheduleByDay[strDay] = []
      }
      scheduleByDay[strDay].push(workSchedule)
    })

    return scheduleByDay
  }, [workScheduleList])

  const newsListPreview = useMemo(() => newsList.slice(0, 4), [newsList])

  const weekPickerValue = useMemo(() => moment(days[0]), [days])

  const defaultActiveTabKey = useMemo(
    () =>
      days
        .map(day => day.getDay())
        .findIndex(day => day === moment().day())
        .toString(),
    [days]
  )

  useEffect(() => {
    setActiveDayTabKey(defaultActiveTabKey)
  }, [defaultActiveTabKey])

  const isAccessControl = useCallback(
    (moduleCode, accessControlType) => {
      if (isSuperAdmin) return true
      if (!moduleStore.checkAccessModule(moduleCode)) return false
      if (accessControlType)
        return (
          isAccountAdmin ||
          !!aclActionsByUser.find(item => item.code === accessControlType)
            ?.status
        )
      return true
    },
    [isSuperAdmin, isAccountAdmin, moduleStore, aclActionsByUser]
  )

  const handleChangeWeek = useCallback((date, type) => {
    if (type === 'prev') {
      setDays(date_utils.weekRange(moment(date[0]).subtract(7, 'days')))
    } else if (type === 'next') {
      setDays(date_utils.weekRange(moment(date[0]).add(7, 'days')))
    } else if (type === 'week') {
      setDays(date_utils.weekRange(date))
    }
  }, [])

  const handlePrevWeek = useCallback(() => handleChangeWeek(days, 'prev'), [
    days,
    handleChangeWeek,
  ])

  const handleNextWeek = useCallback(() => handleChangeWeek(days, 'next'), [
    days,
    handleChangeWeek,
  ])

  const handleWeekPickerChange = useCallback(
    date => handleChangeWeek(date, 'week'),
    [handleChangeWeek]
  )

  const renderFile = useCallback(
    file_ids => {
      if (file_ids) {
        if (file_ids.length === 0) {
          return <EmptyText>Không có tài liệu.</EmptyText>
        }
        if (file_ids.length >= 1) {
          return (
            <DescriptionLink>
              <FileTextOutlined style={{ color: '#40a9ff' }} />
              <a
                onClick={() =>
                  fileStore.handleDownloadFile(
                    file_ids[0].file_id,
                    file_ids[0].file_title
                  )
                }>
                {file_ids[0].file_title}
              </a>
            </DescriptionLink>
          )
        }
      }
    },
    [fileStore]
  )

  const getShipScheduleContent = useCallback(
    workScheduled => {
      const shipScheduleContent = []

      shipScheduleContent.push(
        <div className="work-scheduled-item__info">
          <b>Thời gian: </b>
          {workScheduled.end_at
            ? `Từ ${moment(workScheduled.start_at).format(HHhMM)} Đến ${moment(
                workScheduled.end_at
              ).format(HHhMM)}`
            : `Từ ${moment(workScheduled.start_at).format(HHhMM)}`}
        </div>
      )
      shipScheduleContent.push(
        <div className="work-scheduled-item__info">
          <b>Chủ trì: </b>
          {workScheduled.host ? (
            workScheduled.host
          ) : (
            <EmptyText>Không có chủ trì.</EmptyText>
          )}
        </div>
      )
      shipScheduleContent.push(
        <div className="work-scheduled-item__info">
          <b>Địa điểm: </b>
          <span>
            {workScheduled.location ? (
              UrlifiedText(workScheduled?.location)
            ) : (
              <EmptyText>Không có địa điểm.</EmptyText>
            )}
          </span>
        </div>
      )
      shipScheduleContent.push(
        <div className="work-scheduled-item__info">
          <b>Tài liệu: </b>
          <span>{renderFile(workScheduled?.file_ids)}</span>
        </div>
      )
      shipScheduleContent.push(
        <div className="work-scheduled-item__info">
          <b>Chuẩn bị: </b>
          <span>
            {workScheduled.preparation ? (
              workScheduled.preparation
            ) : (
              <EmptyText>Không có chuẩn bị.</EmptyText>
            )}
          </span>
        </div>
      )
      shipScheduleContent.push(
        <div className="work-scheduled-item__info">
          <b>Nội dung: </b>
          <div>
            {workScheduled.event_notice ? (
              UrlifiedText(
                workScheduled.event_notice
                  .replace(/<\/?[^>]+(>|$)/g, '')
                  .replace(/&nbsp;/g, '')
              )
            ) : (
              <EmptyText>Không có nội dung.</EmptyText>
            )}
          </div>
        </div>
      )
      shipScheduleContent.push(
        <div className="work-scheduled-item__info">
          <b>Thành viên tham gia: </b>
          <div>
            {workScheduled.attenders ? (
              workScheduled.attenders
            ) : (
              <EmptyText>Không có thành viên tham gia.</EmptyText>
            )}
          </div>
        </div>
      )

      return shipScheduleContent
    },
    [renderFile]
  )

  const onClickIncoming = useCallback(() => {
    history.push({
      pathname: `/internal-document/incoming-document`,
      state: {
        from: location.pathname,
        onlyUnread: true,
        page: 0,
        size: 10,
      },
    })
  }, [history, location.pathname])

  const onClickOutgoing = useCallback(() => {
    history.push({
      pathname: `/internal-document/outgoing-document`,
      state: {
        from: location.pathname,
        onlyUnread: true,
        page: 0,
        size: 10,
      },
    })
  }, [history, location.pathname])

  const onClickWorks = useCallback(() => {
    history.push({
      pathname: `/works`,
      state: {
        from: location.pathname,
        isUnread: true,
      },
    })
  }, [history, location.pathname])

  const onClickMission = useCallback(() => {
    history.push({
      pathname: `/mission`,
      state: {
        from: location.pathname,
      },
    })
  }, [history, location.pathname])

  const onClickProposal = useCallback(() => {
    history.push({
      pathname: `/proposal`,
      state: {
        from: location.pathname,
        proposal_Status: 'PENDING',
      },
    })
  }, [history, location.pathname])

  const renderNewsItem = useCallback(
    item => (
      <List.Item
        key={item.id}
        onClick={() => {
          history.push(`/utility/general-notifications/view/${item.id}`)
        }}>
        {item.subject}
      </List.Item>
    ),
    [history]
  )

  const handleOk = useCallback(
    async values => {
      setConfirmLoading(true)
      try {
        const response = await authenticationStore.userLogin(
          values.identifier,
          values.password
        )
        if (response.status === 200) {
          const {
            data: { name_uppercase: nameInUppercase, username },
          } = await authenticationStore.checkCurrentUser()
          console.log('[INFO]  nameInUppercase:', nameInUppercase)
          message.success(
            `Xin chào, ${utils.getNameInCapitalize(nameInUppercase)}!`
          )
          await Promise.allSettled([
            authenticationStore.getCurrentUserAvatar(),
            commandStore.getCommandList(),
            notificationStore.getUnreadNotificationCount(),
            notificationStore.getUnreadNewsCount(),
            userStore.getMentionUserList(),
            accountStore.getCurrentUserAccount(),
            moduleStore.getModuleList(),
            aclStore.getACLDetailByUser(username),
          ])
          setShowDialog(false)
        }
      } catch (err) {
        console.log('[INFO]  err:', err)
      } finally {
        setConfirmLoading(false)
      }
    },
    [
      authenticationStore,
      commandStore,
      notificationStore,
      userStore,
      accountStore,
      moduleStore,
      aclStore,
    ]
  )

  useEffect(() => {
    ;(async () => {
      try {
        if (!currentUser) return
        setIsLoadingTinTuc(true)
        await tintucStore.getAllNews()
        await tintucStore.getReadNewsByUsername(currentUser?.username)
        setNewsList(tintucStore.newsList)
      } catch (err) {
        console.log(err)
        message.error(err.vi || 'Đã có lỗi xảy ra!')
      } finally {
        setIsLoadingTinTuc(false)
      }
    })()
  }, [currentUser, tintucStore])

  useEffect(() => {
    workScheduleStore.getWorkSchedulePermissionView()
  }, [])

  useEffect(() => {
    if (!companyList?.length || hasInitializedSelectedKey.current) return

    const infoOptionList = infoOptionStore.infoOptionList || {}
    const isInfoOptionReady =
      infoOptionList.IMSAT_COMPANIES ||
      infoOptionList.CODE ||
      Object.keys(infoOptionList).length > 0

    if (!isInfoOptionReady) return

    setSelectedKey(
      resolveWorkScheduleMenuKey(
        getDefaultWorkScheduleCompanyKey({
          companyList,
          imsatCompanies,
          currentUserCompanyCode: currentUser?.company?.code,
        }),
        imsatCompanies
      )
    )
    hasInitializedSelectedKey.current = true
  }, [
    companyList,
    imsatCompanies,
    currentUser?.company?.code,
    infoOptionStore.infoOptionList,
  ])

  useEffect(() => {
    if (!selectedKey) return

    ;(async () => {
      try {
        setIsLoadingCalendar(true)
        const fromDate = moment(days[0]).format(ISO_DATE_FORMAT)
        const toDate = moment(days[6]).format(ISO_DATE_FORMAT)

        const data = await fetchWorkScheduleList({
          workScheduleStore,
          parentWorkScheduleStore,
          fromDate,
          toDate,
          selectedKey,
          companyList,
          imsatCompanies,
          infoOptionList: infoOptionStore.infoOptionList,
          currentUserCompanyCode: currentUser?.company?.code,
        })

        setWorkScheduledList(data)
      } catch (error) {
        console.log(error)
        message.error(error?.vi || 'Đã có lỗi xảy ra!')
      } finally {
        setIsLoadingCalendar(false)
      }
    })()

    return () => {
      workScheduleStore.clearWorkSchedule()
      parentWorkScheduleStore.clearWorkSchedule()
    }
  }, [
    days,
    selectedKey,
    companyList,
    imsatCompanies,
    infoOptionStore.infoOptionList,
    currentUser?.company?.code,
  ])

  useEffect(() => {
    return () => {
      statisticStore.clearStatistic()
      tintucStore.clear()
      commandStore.clearStore()
    }
  }, [])

  const loginPage = useMemo(
    () => (
      <>
        <LoginWrapper
          bgImage={`${process.env.PUBLIC_URL}/assets/photos/auth-bg-2.jpg`}>
          {useSSO ? (
            <></>
          ) : (
            <Form layout={'vertical'} name={'loginForm'} onFinish={handleOk}>
              <Form.Item style={{ textAlign: 'center' }}>
                <img
                  alt="login_logo"
                  height={32}
                  src={`${process.env.PUBLIC_URL}/assets/photos/portal_logo_white.png`}
                />
              </Form.Item>
              <Form.Item
                label="Tên đăng nhập"
                name="identifier"
                rules={[
                  { required: true, message: 'Vui lòng nhập tên đăng nhập!' },
                  { validator: validator.validateUsername },
                ]}>
                <Input />
              </Form.Item>
              <Form.Item
                label="Mật khẩu"
                name="password"
                rules={[
                  { required: true, message: 'Vui lòng nhập mật khẩu!' },
                ]}>
                <Input.Password />
              </Form.Item>
              <Button
                type={'primary'}
                block
                htmlType={'submit'}
                loading={confirmLoading}>
                {' '}
                Đăng nhập
              </Button>

              <Button style={{ marginTop: 8 }} type={'link'} block>
                {' '}
                Quên mật khẩu?
              </Button>
            </Form>
          )}
        </LoginWrapper>
      </>
    ),
    [confirmLoading, handleOk]
  )

  return (
    <div>
      {currentUser != null ? (
        <DashboardLayout
          title={'Link People - Link the World'}
          backgroundColor={'#f2f3f8'}>
          <Helmet>
            <title>Dashboard | {systemName} Portal</title>
          </Helmet>
          <div style={{ position: 'relative' }}>
            <RowWrapper>
              <CardWrapper>
                {isAccessControl(
                  MODULE_CODE.van_ban_noi_bo,
                  ACL_ACTION_TYPE.menu__INTERNAL_DOCUMENT
                ) && (
                  <CounterCard
                    title={'Văn bản đến chưa đọc'}
                    counter={sideMenuCounter?.unread_incoming ?? 0}
                    icon={`${process.env.PUBLIC_URL}/assets/icons/document_outgoing_new.svg`}
                    onClick={onClickIncoming}>
                    <SpinnerInlineComponent
                      sizeSpin={'small'}
                      isLoading={isLoadingSidemenuCounter}
                      backGround={'#ffffffc7'}
                    />
                  </CounterCard>
                )}

                {isAccessControl(
                  MODULE_CODE.van_ban_noi_bo,
                  ACL_ACTION_TYPE.menu__INTERNAL_DOCUMENT
                ) && (
                  <CounterCard
                    title={'Văn bản đi chưa đọc'}
                    counter={sideMenuCounter?.unread_outgoing ?? 0}
                    icon={`${process.env.PUBLIC_URL}/assets/icons/document_incoming_new.svg`}
                    onClick={onClickOutgoing}>
                    <SpinnerInlineComponent
                      sizeSpin={'small'}
                      isLoading={isLoadingSidemenuCounter}
                      backGround={'#ffffffc7'}
                    />
                  </CounterCard>
                )}
                {isAccessControl(
                  MODULE_CODE.cong_viec,
                  ACL_ACTION_TYPE.menu__WORK
                ) && (
                  <CounterCard
                    title={'Công việc chưa đọc'}
                    counter={sideMenuCounter?.work ?? 0}
                    icon={`${process.env.PUBLIC_URL}/assets/icons/work_manager_draft.svg`}
                    onClick={onClickWorks}>
                    <SpinnerInlineComponent
                      sizeSpin={'small'}
                      isLoading={isLoadingSidemenuCounter}
                      backGround={'#ffffffc7'}
                    />
                  </CounterCard>
                )}
                {isAccessControl(
                  MODULE_CODE.nhiem_vu,
                  ACL_ACTION_TYPE.menu__MISSION
                ) && (
                  <CounterCard
                    title={'Nhiệm vụ quá hạn'}
                    counter={sideMenuCounter?.overdue_task ?? 0}
                    icon={`${process.env.PUBLIC_URL}/assets/icons/work_manager_overdue.svg`}
                    onClick={onClickMission}>
                    <SpinnerInlineComponent
                      sizeSpin={'small'}
                      isLoading={isLoadingSidemenuCounter}
                      backGround={'#ffffffc7'}
                    />
                  </CounterCard>
                )}
                {isAccessControl(
                  MODULE_CODE.hanh_chinh,
                  ACL_ACTION_TYPE.menu__PROPOSAL
                ) && (
                  <CounterCard
                    title={'Đơn hành chính chờ duyệt'}
                    counter={sideMenuCounter?.proposal_other ?? 0}
                    icon={`${process.env.PUBLIC_URL}/assets/icons/work_manager_processing.svg`}
                    onClick={onClickProposal}>
                    <SpinnerInlineComponent
                      sizeSpin={'small'}
                      isLoading={isLoadingSidemenuCounter}
                      backGround={'#ffffffc7'}
                    />
                  </CounterCard>
                )}
              </CardWrapper>
              {isAccessControl(
                MODULE_CODE.thong_bao_chung,
                ACL_ACTION_TYPE.menu__NEWS
              ) && (
                <GeneralNotif
                  header={<div>Thông báo chung </div>}
                  footer={false}
                  bordered
                  dataSource={newsListPreview}
                  renderItem={renderNewsItem}>
                  <SpinnerInlineComponent
                    sizeSpin={'small'}
                    isLoading={isLoadingTinTuc}
                    backGround={'#ffffffc7'}
                    tip={'Đang tải thông báo chung...'}
                  />
                </GeneralNotif>
              )}
            </RowWrapper>
            {isAccessControl(
              MODULE_CODE.lich_co_quan,
              ACL_ACTION_TYPE.menu__WORK_SCHEDULE
            ) && (
              <WorkScheduledCard
                title={'Lịch cơ quan'}
                extra={
                  <div>
                    <Tooltip title={'Tuần trước'}>
                      <Button
                        onClick={handlePrevWeek}
                        icon={<LeftOutlined />}
                      />
                    </Tooltip>
                    <DatePicker
                      allowClear={false}
                      onChange={handleWeekPickerChange}
                      picker="week"
                      placeholder={'Chọn tuần'}
                      format={'Tuần w, YYYY'}
                      value={weekPickerValue}
                      style={{
                        height: 32,
                        width: 120,
                        margin: '0 10px 0 10px',
                      }}
                    />
                    <Tooltip title={'Tuần sau'}>
                      <Button
                        onClick={handleNextWeek}
                        icon={<RightOutlined />}
                      />
                    </Tooltip>
                  </div>
                }>
                <Tabs
                  centered
                  activeKey={activeDayTabKey || defaultActiveTabKey}
                  onChange={setActiveDayTabKey}
                  size={'small'}>
                  {days.map((day, dayIndex) => {
                    const strDay = moment(day).format(DD_MM)
                    const daySchedules = workScheduleByDay[strDay] || []
                    const dayTab =
                      dayIndex !== 6 ? (
                        <DayTab>
                          <p className="weekday">{`Thứ ${dayIndex + 2}`}</p>
                          <p className="monthday">{`(${strDay})`}</p>
                        </DayTab>
                      ) : (
                        <DayTab>
                          <p>Chủ nhật</p>
                          <p> {`(${strDay})`}</p>
                        </DayTab>
                      )

                    return (
                      <TabPane tab={dayTab} key={dayIndex}>
                        <ItemCalendarContent>
                          {daySchedules.length !== 0 ? (
                            daySchedules.map(workScheduled => {
                              const scheduleContent = getShipScheduleContent(
                                workScheduled
                              )

                              return (
                                <WorkScheduledItem
                                  key={workScheduled.schedule_code}>
                                  <div className="">
                                    {scheduleContent.slice(0, 5)}
                                  </div>
                                  <div className="">
                                    {scheduleContent.slice(5)}
                                  </div>
                                </WorkScheduledItem>
                              )
                            })
                          ) : !isLoadingCalendar ? (
                            <EmptyContent description={'Không có sự kiện!'} />
                          ) : (
                            <div style={{ height: 106 }} />
                          )}
                          <SpinnerInlineComponent
                            sizeSpin={'small'}
                            isLoading={isLoadingCalendar}
                            noBackground
                            tip={'Đang tải lịch cơ quan...'}
                          />
                        </ItemCalendarContent>
                      </TabPane>
                    )
                  })}
                </Tabs>
              </WorkScheduledCard>
            )}
            {/* {updatePassword} */}
          </div>
        </DashboardLayout>
      ) : (
        loginPage
      )}
    </div>
  )
}

export default memo(
  inject(
    'workScheduleStore',
    'parentWorkScheduleStore',
    'loadingAnimationStore',
    'commonStore',
    'statisticStore',
    'fileStore',
    'authenticationStore',
    'accountStore',
    'userStore',
    'companyStore',
    'notificationStore',
    'commandStore',
    'taskManagementStore',
    'taskManagementCommentStore',
    'moduleStore',
    'tintucStore',
    'aclStore',
    'infoOptionStore'
  )(observer(DashboardPageV2))
)
